/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { getEventId } from "Router/RoutesUtils";
import SpacingTokens from "theming/tokens/SpacingTokens.json";
import Layout from "./Layout";

import { queryClientOptions } from "lib/utils/reactQueryConfig";
import {
  AuthProvider,
  RoleProvider,
  UIProvider,
  store,
  PrimaryEventsProvider,
  GeneralProvider,
  ImportContextProvider,
} from "state";

import "lib/utils/i18next";
import FavIcon from "components/ui/FavIcon/FavIcon";
import { EnvManager } from "state/EnvironmentsContext/EnvManager";
import { LoadingProvider } from "state/LoadingContext";

import "./styles/index.css";
import React from "react";
import FeatureFlagManager from "state/FeaturesFlagContext/FeatureFlagManager";
import { SafeComponent } from "Router/RouterManagers/RouteManager";
import * as Sentry from "@sentry/react";
import setupSentry from "./constants/sentry.config";
import { sentryCaptureError } from "utils/errorFormatter";
import * as Tooltip from "@radix-ui/react-tooltip";
export const queryClient = new QueryClient(queryClientOptions);
const eventId = getEventId();

// style element with :root tokens
const style = document.createElement("style");
style.textContent = `
  :root {
    ${Object.entries(SpacingTokens)
      .map(([key, value]) => `${key}: ${value} !important;`)
      .join("\n")}
  }
`;

document.head.appendChild(style);
// setup sentry in the root of the app
setupSentry();

window.addEventListener("error", (event) => {
  event.preventDefault();
  sentryCaptureError(event.error, "Global Error Handler");
});

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary>
      <BrowserRouter basename={eventId ?? ""}>
        <QueryClientProvider client={queryClient}>
          <GeneralProvider>
            <RoleProvider>
              <AuthProvider>
                <EnvManager>
                  <FeatureFlagManager>
                    <ImportContextProvider>
                      <PrimaryEventsProvider>
                        <UIProvider>
                          <LoadingProvider>
                            <Provider store={store}>
                              <Tooltip.Provider>
                                <FavIcon />
                                <SafeComponent>
                                  <Layout />
                                </SafeComponent>

                                <Toaster
                                  toastOptions={{
                                    duration: 2500,
                                    style: {
                                      borderRadius: "10px",
                                      background: "#333",
                                      color: "#fff",
                                    },
                                  }}
                                  containerStyle={{
                                    top: "70px",
                                  }}
                                />
                              </Tooltip.Provider>
                            </Provider>
                          </LoadingProvider>
                        </UIProvider>
                      </PrimaryEventsProvider>
                    </ImportContextProvider>
                  </FeatureFlagManager>
                </EnvManager>
              </AuthProvider>
            </RoleProvider>
          </GeneralProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
